import { isEqual } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { IDashboardChartData, IDashboardChartDataKey } from '~/src/dash/api/dashboards/home';
import { IDashboardSubChartMetric } from '~/src/dash/components/charts/common';
import { MultiLineChart } from '~/src/dash/components/charts/MultiLineChart';
import {
  IndependentSelectV2,
  Row,
  Rows,
  spacing,
  Text,
  TKey,
  useIsMounted,
} from '~/src/dash/publicaUI';
import { formatHugeNumberToShortenedString } from '~/src/dash/utils/format';
import { LoadingState } from '~/src/dash/utils/loading-states';
import {
  IActiveFilters,
  subChartColorKeys,
  subChartColors,
} from '~/src/dash/views/dashboards/DashboardHome/common/common';

interface IDashboardSubChartMetricsSelectorProps {
  title: string;
  data: Partial<IDashboardChartData>[];
  loadState: LoadingState;
  metricsCfg: Record<string, IDashboardSubChartMetric>;
  orderedMetricKeys: [IDashboardChartDataKey, number][];
  activeFilters: IActiveFilters;
  defaultActiveMetrics?: string[];
  syncID?: string;
  tKey?: TKey;
}

export const DashboardSubChartMetricsSelector = ({
  title,
  tKey,
  data = [],
  loadState,
  metricsCfg,
  orderedMetricKeys,
  defaultActiveMetrics = [],
  syncID,
}: IDashboardSubChartMetricsSelectorProps): React.JSX.Element => {
  const isMounted = useIsMounted();
  const [activeMetrics, setActiveMetrics] = useState<IDashboardSubChartMetric[]>(
    defaultActiveMetrics.map((key) => metricsCfg[key]),
  );
  const [dropdownKey, setDropdownKey] = useState<number>(0);
  const [prevDefaultActiveMetrics, setPrevDefaultActiveMetrics] =
    useState<string[]>(defaultActiveMetrics);

  // On Mount populate the active metrics with the default top 5 active metrics
  // If external active filters change, reset the active metrics to new top 5
  useEffect(() => {
    if (
      isMounted() &&
      (activeMetrics.length === 0 || !isEqual(prevDefaultActiveMetrics, defaultActiveMetrics))
    ) {
      setActiveMetrics(
        defaultActiveMetrics.map((key, i) => {
          // Object.keys order is guaranteed to be stable as long as the object is not mutated
          const colorKey = Object.keys(subChartColors)[
            i % Object.keys(subChartColors).length
          ] as subChartColorKeys;
          metricsCfg[key].color = subChartColors[colorKey];
          return metricsCfg[key];
        }),
      );
      setPrevDefaultActiveMetrics(defaultActiveMetrics);
      // Force a re-render of the dropdown
      setDropdownKey(dropdownKey + 1);
    }
  }, [defaultActiveMetrics]);

  const handleApplySelect = (selectedIDs: Set<string>): void => {
    const newActiveMetrics: IDashboardSubChartMetric[] = [];
    // convert set to array
    selectedIDs.forEach((id: string) => {
      newActiveMetrics.push(metricsCfg[id]);
    });
    // sort by count descending
    newActiveMetrics.sort((a, b) => {
      return (
        (orderedMetricKeys.find(([key]) => key === b.key)?.[1] ?? 0) -
        (orderedMetricKeys.find(([key]) => key === a.key)?.[1] ?? 0)
      );
    });
    // set color based on sorted index
    newActiveMetrics.forEach((metric, index) => {
      const colorKey = Object.keys(subChartColors)[
        index % Object.keys(subChartColors).length
      ] as subChartColorKeys;
      metric.color = subChartColors[colorKey];
    });
    setActiveMetrics(newActiveMetrics);
  };

  return (
    <Rows>
      <Row style={{ padding: `${spacing.small} ${spacing.base} 0` }}>
        <Text small bold tKey={tKey}>
          {title}
        </Text>
      </Row>
      <Row smallVerticalPadding>
        <IndependentSelectV2
          handleApplySelect={handleApplySelect}
          labelTKey='Metrics'
          options={orderedMetricKeys.map(([key, count]) => {
            const metric = metricsCfg[key];
            return {
              id: metric.key,
              label: metric.label + ' (' + formatHugeNumberToShortenedString(count) + ')',
              sortValue: -count,
            };
          })}
          selectedLimit={Infinity}
          selectedOptionIDs={new Set(activeMetrics.map((item) => item.key))}
          key={dropdownKey}
          selectKey='metrics'
          maxWidth={280}
          popoverWidth={580}
          textWidth={532}
          extractStringToSort={(option) => {
            return option.sortValue ? option.sortValue : option.label;
          }}
        />
      </Row>
      <Row>
        <MultiLineChart
          maxTooltipItems={8}
          loadState={loadState}
          data={data}
          metrics={activeMetrics}
          metricsCfg={metricsCfg}
          syncID={syncID}
        />
      </Row>
    </Rows>
  );
};
